import React, { useEffect } from 'react';
import TrustSection from '../TrustSection';
import PageHero from '../PageHero';
import { Shield, Target, Heart, Eye, Star, Sparkles, CheckCircle2, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import tekkeSangliImg from '../../assets/tekke-sangli.jpeg';

const About = () => {
  const { t } = useLanguage();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-[#F8FAFC] min-h-screen">
      {/* Attractive Hero Banner matching Branches.js */}
      <PageHero
        badge={t('nav.about')}
        title={`${t('aboutSection.heroTitle1')} ${t('aboutSection.heroTitle2')}.`}
        subtitle={t('aboutSection.heroP1')}
      />

      {/* Main Content overlapping hero */}
      <div className="container mx-auto px-4 lg:px-8 -mt-16 relative z-20 mb-20">
        <div className="bg-white rounded-3xl p-8 lg:p-12 shadow-sm border border-gray-200 hover:border-gray-300 transition-colors">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="w-full lg:w-1/2">
              <div className="flex items-center space-x-3 mb-4">
                <span className="w-8 h-[2px] bg-teal"></span>
                <span className="text-teal font-bold tracking-widest text-xs uppercase">{t('aboutSection.guidingPrinciples')}</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-extrabold text-navy mb-6 tracking-tight">
                {t('aboutSection.heroTitle1')} <span className="text-clinical">{t('aboutSection.heroTitle2')}</span>
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                {t('aboutSection.heroP1')}
              </p>
              <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-8">
                {t('aboutSection.heroP2')}
              </p>
              <Link 
                to="/doctors" 
                className="inline-flex items-center gap-2 bg-navy hover:bg-clinical text-white px-6 py-3 rounded-full font-semibold transition-colors shadow-sm"
              >
                <span>{t('aboutSection.meetDoctors')}</span>
                <ArrowRight size={18} />
              </Link>
            </div>
            <div className="w-full lg:w-1/2">
              <div className="relative rounded-2xl overflow-hidden shadow-lg border border-gray-100">
                <img 
                  src={tekkeSangliImg} 
                  alt="Sree Tekke Eye Clinic Sangli" 
                  className="w-full h-[380px] object-cover hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/40 to-transparent pointer-events-none"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <TrustSection />

      {/* Mission & Vision Section */}
      <section className="py-24 bg-gradient-to-b from-[#F8FAFC] to-[#EFF6FF]/60 relative overflow-hidden">
        {/* Subtle decorative background shapes */}
        <div className="absolute top-10 left-10 w-72 h-72 bg-amber-100/40 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-10 right-10 w-80 h-80 bg-clinical/10 rounded-full blur-3xl pointer-events-none"></div>

        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-teal/10 border border-teal/20 text-teal text-xs font-bold uppercase tracking-wider mb-4">
              <Sparkles size={14} className="text-teal" />
              <span>{t('aboutSection.guidingPrinciples')}</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-extrabold text-navy tracking-tight">
              {t('aboutSection.missionVisionTitle')}
            </h2>
            <p className="text-gray-600 mt-4 text-base md:text-lg">
              {t('aboutSection.missionVisionSub')}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* Our Mission Card */}
            <div className="relative group bg-white rounded-3xl p-8 md:p-10 border border-amber-200/90 shadow-xl shadow-amber-500/5 hover:shadow-2xl hover:shadow-amber-500/10 transition-all duration-300 flex flex-col justify-between overflow-hidden">
              <div className="absolute top-0 right-0 w-36 h-36 bg-gradient-to-bl from-amber-100/60 to-transparent rounded-bl-full pointer-events-none transition-transform group-hover:scale-110"></div>
              
              <div>
                <div className="flex items-center justify-between mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-tr from-amber-400 to-amber-200 text-white flex items-center justify-center shadow-lg shadow-amber-400/30 group-hover:scale-110 transition-transform duration-300">
                    <Star size={32} className="fill-white" />
                  </div>
                  <span className="text-[11px] font-bold tracking-widest uppercase px-3 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                    {t('aboutSection.ourPurpose')}
                  </span>
                </div>

                <h3 className="text-2xl md:text-3xl font-extrabold text-navy mb-4 flex items-center gap-2">
                  <span>{t('aboutSection.missionTitle')}</span>
                </h3>

                <p className="text-gray-700 leading-relaxed text-base md:text-[17px] mb-8 font-normal">
                  {t('aboutSection.missionText')}
                </p>
              </div>

              <div className="pt-6 border-t border-amber-100/80 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="flex items-center gap-2 text-xs font-semibold text-gray-700 bg-amber-50/70 px-3 py-2 rounded-xl">
                  <CheckCircle2 size={16} className="text-amber-500 shrink-0" />
                  <span>{t('aboutSection.tagAccurate')}</span>
                </div>
                <div className="flex items-center gap-2 text-xs font-semibold text-gray-700 bg-amber-50/70 px-3 py-2 rounded-xl">
                  <CheckCircle2 size={16} className="text-amber-500 shrink-0" />
                  <span>{t('aboutSection.tagEthical')}</span>
                </div>
                <div className="flex items-center gap-2 text-xs font-semibold text-gray-700 bg-amber-50/70 px-3 py-2 rounded-xl">
                  <CheckCircle2 size={16} className="text-amber-500 shrink-0" />
                  <span>{t('aboutSection.tagAffordable')}</span>
                </div>
              </div>
            </div>

            {/* Our Vision Card */}
            <div className="relative group bg-white rounded-3xl p-8 md:p-10 border border-blue-200/90 shadow-xl shadow-clinical/5 hover:shadow-2xl hover:shadow-clinical/10 transition-all duration-300 flex flex-col justify-between overflow-hidden">
              <div className="absolute top-0 right-0 w-36 h-36 bg-gradient-to-bl from-blue-100/60 to-transparent rounded-bl-full pointer-events-none transition-transform group-hover:scale-110"></div>
              
              <div>
                <div className="flex items-center justify-between mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-tr from-clinical to-teal text-white flex items-center justify-center shadow-lg shadow-clinical/30 group-hover:scale-110 transition-transform duration-300">
                    <Eye size={32} />
                  </div>
                  <span className="text-[11px] font-bold tracking-widest uppercase px-3 py-1 rounded-full bg-blue-50 text-clinical border border-blue-200">
                    {t('aboutSection.ourAspiration')}
                  </span>
                </div>

                <h3 className="text-2xl md:text-3xl font-extrabold text-navy mb-4 flex items-center gap-2">
                  <span>{t('aboutSection.visionTitle')}</span>
                </h3>

                <p className="text-gray-700 leading-relaxed text-base md:text-[17px] mb-8 font-normal">
                  {t('aboutSection.visionText')}
                </p>
              </div>

              <div className="pt-6 border-t border-blue-100/80 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="flex items-center gap-2 text-xs font-semibold text-gray-700 bg-blue-50/70 px-3 py-2 rounded-xl">
                  <CheckCircle2 size={16} className="text-clinical shrink-0" />
                  <span>{t('aboutSection.tagExcellence')}</span>
                </div>
                <div className="flex items-center gap-2 text-xs font-semibold text-gray-700 bg-blue-50/70 px-3 py-2 rounded-xl">
                  <CheckCircle2 size={16} className="text-clinical shrink-0" />
                  <span>{t('aboutSection.tagAdvanced')}</span>
                </div>
                <div className="flex items-center gap-2 text-xs font-semibold text-gray-700 bg-blue-50/70 px-3 py-2 rounded-xl">
                  <CheckCircle2 size={16} className="text-clinical shrink-0" />
                  <span>{t('aboutSection.tagCommunity')}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Three Core Pillars: Excellence, Compassion, Technology */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12 max-w-6xl mx-auto">
            <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl border border-gray-100 shadow-sm flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-teal/10 text-teal flex items-center justify-center shrink-0">
                <Shield size={24} />
              </div>
              <div>
                <h4 className="font-bold text-navy text-base mb-1">{t('aboutSection.tagExcellence')}</h4>
                <p className="text-gray-600 text-xs leading-relaxed">
                  {t('features.f1Desc')}
                </p>
              </div>
            </div>

            <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl border border-gray-100 shadow-sm flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-red-50 text-red-500 flex items-center justify-center shrink-0">
                <Heart size={24} />
              </div>
              <div>
                <h4 className="font-bold text-navy text-base mb-1">{t('features.f2Title')}</h4>
                <p className="text-gray-600 text-xs leading-relaxed">
                  {t('features.f2Desc')}
                </p>
              </div>
            </div>

            <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl border border-gray-100 shadow-sm flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-50 text-clinical flex items-center justify-center shrink-0">
                <Target size={24} />
              </div>
              <div>
                <h4 className="font-bold text-navy text-base mb-1">{t('aboutSection.tagEthical')}</h4>
                <p className="text-gray-600 text-xs leading-relaxed">
                  {t('hero.tagline')}
                </p>
              </div>
            </div>
          </div>

        </div>
      </section>
    </div>
  );
};

export default About;
